<template>
  <div>
    <v-btn text small class="mb-4 px-0" style="color: #777" @click="$router.back()">
      <v-icon small left>mdi-arrow-left</v-icon> Volver
    </v-btn>

    <div class="d-flex align-center mb-6">
      <div>
        <div class="text-h6 font-weight-medium white--text">Movimientos de cuenta</div>
        <div class="caption" style="color: #aaa">
          Conocé el detalle de las operaciones que impactan en tu cuenta corriente.
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn
        text
        small
        :loading="loadingPDF"
        style="color: #777"
        @click="descargarPDF"
      >
        <v-icon small left>mdi-file-pdf-box</v-icon> Resumen PDF
      </v-btn>
    </div>

    <!-- Filtros -->
    <div class="d-flex flex-wrap align-center mb-4" style="gap: 8px">
      <v-select
        v-model="filtros.tipo"
        :items="tiposOpciones"
        label="Tipo"
        clearable
        dense
        outlined
        hide-details
        style="max-width: 150px; background: #1e1e1e"
        dark
      ></v-select>

      <v-select
        v-if="sucursales.length > 0"
        v-model="filtros.sucursal"
        :items="sucursales"
        item-text="nombre"
        item-value="id"
        label="Sucursal"
        clearable
        dense
        outlined
        hide-details
        style="max-width: 180px; background: #1e1e1e"
        dark
      ></v-select>

      <date-picker-multiple @process-select="setDateFilter"></date-picker-multiple>

      <v-btn
        v-if="tieneFiltrosActivos"
        text
        small
        style="color: #777"
        @click="limpiarFiltros"
      >
        <v-icon small left>mdi-close</v-icon> Limpiar
      </v-btn>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="grey darken-1" class="mb-2"></v-progress-linear>

    <template v-if="!loading && movimientos.length === 0">
      <div class="caption text-center py-8" style="color: #666">Sin movimientos registrados.</div>
    </template>

    <div
      v-for="item in movimientos"
      :key="item.id"
      class="movimiento-row d-flex align-center py-3 px-2"
      :class="{ clickable: !!(item.comprobante || item.recibo) }"
      @click="irAlDetalle(item)"
    >
      <v-icon size="18" :color="tipoConfig(item.tipo).color" class="mr-3">
        {{ tipoConfig(item.tipo).icon }}
      </v-icon>

      <div class="flex-grow-1 overflow-hidden">
        <div class="body-2" style="color: #e0e0e0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis">
          {{ item.descripcion }}
        </div>
        <div class="caption mt-1" style="color: #777">
          <span>{{ moment(item.fecha).format("ll") }}</span>
          <span v-if="item.sucursal_nombre"> · {{ item.sucursal_nombre }}</span>
        </div>
      </div>

      <div class="text-right ml-4" style="min-width: 110px">
        <div v-if="item.debe > 0" class="body-2 font-weight-medium" style="color: #ff6b6b">
          - {{ item.debe | convertPesoArg }}
        </div>
        <div v-if="item.haber > 0" class="body-2 font-weight-medium" style="color: #69db7c">
          + {{ item.haber | convertPesoArg }}
        </div>
        <div class="caption mt-1" style="color: #555">{{ item.saldo | convertPesoArg }}</div>
      </div>
    </div>

    <v-pagination
      v-if="pages > 1"
      class="mt-4"
      @input="paginationChangeHandler"
      :length="pages"
      :value="paginacion.page"
      circle
    ></v-pagination>
  </div>
</template>

<script>
import ClienteCuentaCorrienteAPI from "@/api/clientes/cuentacorriente.js";
import DatePickerMultiple from "@/components/reportes/DatePickerMultiple";
import { mapActions, mapState } from "vuex";

export default {
  name: "ClienteCuentaCorrienteMovimientos",
  components: { DatePickerMultiple },
  data() {
    return {
      paginacion: { page: 1, cantidad: 0, rowsPerPage: 12 },
      loading: false,
      loadingPDF: false,
      movimientos: [],
      filtros: {
        tipo: null,
        sucursal: null,
        fecha_desde: null,
        fecha_hasta: null,
      },
      tiposOpciones: ["RECIBO", "NV", "FC", "NC", "ND"],
    };
  },
  computed: {
    ...mapState("sucursales", ["sucursales"]),
    esContextoPos() {
      const name = this.$route.name || "";
      const path = this.$route.path || "";
      return name.includes("pos") || path.includes("/pos");
    },
    pages() {
      if (!this.paginacion.cantidad) return 0;
      return Math.ceil(this.paginacion.cantidad / this.paginacion.rowsPerPage);
    },
    tieneFiltrosActivos() {
      return Object.values(this.filtros).some((v) => !!v);
    },
  },
  watch: {
    filtros: {
      handler() {
        this.paginacion.page = 1;
        this.fetchMovimientos();
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions("sucursales", ["fetch_sucursales"]),
    tipoConfig(tipo) {
      const configs = {
        RECIBO: { icon: "mdi-arrow-down-circle-outline", color: "#69db7c" },
        NV:     { icon: "mdi-arrow-up-circle-outline",   color: "#ff6b6b" },
        FC:     { icon: "mdi-arrow-up-circle-outline",   color: "#ff6b6b" },
        NC:     { icon: "mdi-minus-circle-outline",      color: "#74c0fc" },
        ND:     { icon: "mdi-plus-circle-outline",       color: "#ff6b6b" },
      };
      return configs[tipo] || { icon: "mdi-circle-outline", color: "#555" };
    },
    irAlDetalle(item) {
      if (!item.comprobante && !item.recibo) return;
      const prefix = this.esContextoPos ? "pos" : "empresa";
      this.$router.push({
        name: `${prefix}.cliente.cuentacorriente.movimientos.detail`,
        params: { movimiento_id: item.id },
      });
    },
    paginationChangeHandler(pageNumber) {
      if (this.paginacion.page === pageNumber) return;
      this.paginacion.page = pageNumber;
      this.fetchMovimientos();
    },
    setDateFilter(filter) {
      const params = new URLSearchParams(filter);
      this.filtros.fecha_desde = params.get("from_current") || null;
      this.filtros.fecha_hasta = params.get("to_current") || null;
    },
    descargarPDF() {
      this.loadingPDF = true;
      ClienteCuentaCorrienteAPI.descargarResumenPDF(
        this.$route.params.id_cliente,
        this.filtros
      ).then((response) => {
        const url = URL.createObjectURL(response.data);
        window.open(url);
        URL.revokeObjectURL(url);
      }).catch(() => {
        this.$updateBus.$emit("showSnackBar", {
          show: true,
          button: false,
          color: "error",
          text: "No se pudo generar el PDF.",
          icon: "mdi-alert",
          timeout: 3000,
        });
      }).finally(() => {
        this.loadingPDF = false;
      });
    },
    limpiarFiltros() {
      this.filtros = { tipo: null, sucursal: null, fecha_desde: null, fecha_hasta: null };
    },
    fetchMovimientos() {
      this.loading = true;
      ClienteCuentaCorrienteAPI.movimientos_cta_cte(
        this.$route.params.id_cliente,
        this.paginacion,
        this.filtros
      ).then((response) => {
        this.movimientos = response.data.results;
        this.paginacion.cantidad = response.data.count;
      }).finally(() => {
        this.loading = false;
      });
    },
  },
  created() {
    this.fetchMovimientos();
    if (this.sucursales.length === 0) {
      this.fetch_sucursales();
    }
  },
};
</script>

<style scoped>
.movimiento-row {
  border-bottom: 1px solid #2a2a2a;
  transition: background 0.15s;
}
.movimiento-row:last-child {
  border-bottom: none;
}
.movimiento-row.clickable:hover {
  background: #1e1e1e;
  cursor: pointer;
}
</style>
